/**
 * Tab storage for the campaign the visitor landed with.
 * A later page that has no UTMs must not wipe these, and must not be
 * recorded as organic when the form opens.
 */

export const LANDING_PARAMS_KEY = 'uh_landing_params';

export const LANDING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const;

export type LandingParamKey = (typeof LANDING_PARAM_KEYS)[number];
export type LandingParams = Partial<Record<LandingParamKey, string>>;

const CLICK_IDS = ['gclid', 'fbclid'] as const;
type ClickId = (typeof CLICK_IDS)[number];

/** Click ids for this document. Not written to the browser until ads consent. */
let clickMemory: Partial<Record<ClickId, string>> = {};

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function readLandingParams(): LandingParams {
  const store = storage();
  if (!store) return {};
  try {
    const raw = store.getItem(LANDING_PARAMS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: LandingParams = {};
    for (const key of LANDING_PARAM_KEYS) {
      const value = parsed[key];
      if (typeof value === 'string' && value) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function isClickId(key: LandingParamKey): key is ClickId {
  return key === 'gclid' || key === 'fbclid';
}

/** Keep UTMs in the tab. Click ids stay in memory until persistLandingClickIds. */
export function rememberLandingParams(search: string): LandingParams {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  const stored = readLandingParams();
  for (const key of CLICK_IDS) {
    if (!clickMemory[key] && stored[key]) clickMemory[key] = stored[key];
  }
  const next: LandingParams = { ...stored };
  let changed = false;
  for (const key of LANDING_PARAM_KEYS) {
    const value = params.get(key);
    if (!value) continue;
    if (isClickId(key)) {
      if (clickMemory[key] !== value) clickMemory[key] = value;
      continue;
    }
    if (next[key] === value) continue;
    next[key] = value;
    changed = true;
  }
  if (changed) writeLandingParams(next);
  return { ...next, ...clickMemory };
}

/** Write the in-memory click ids after ads consent. A reload can then restore them. */
export function persistLandingClickIds(): void {
  const next: LandingParams = { ...readLandingParams() };
  let changed = false;
  for (const key of CLICK_IDS) {
    const value = clickMemory[key];
    if (!value || next[key] === value) continue;
    next[key] = value;
    changed = true;
  }
  if (changed) writeLandingParams(next);
}

function writeLandingParams(next: LandingParams): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(LANDING_PARAMS_KEY, JSON.stringify(next));
  } catch {
    // Session storage can be blocked. The URL still wins for this page.
  }
}

/**
 * Click ids for the Typeform. Empty until ads consent, so the form does not
 * receive an ad identifier before a choice.
 */
export function landingClickIds(
  search: string,
  adsAllowed: boolean,
): Record<string, string> {
  rememberLandingParams(search);
  if (!adsAllowed) return {};
  persistLandingClickIds();
  const out: Record<string, string> = {};
  for (const key of CLICK_IDS) {
    const value = clickMemory[key];
    if (value) out[key] = value;
  }
  return out;
}

export function resetLandingParamsForTests(): void {
  clickMemory = {};
  storage()?.removeItem(LANDING_PARAMS_KEY);
}
